import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { projectService } from '../../services/projectService';
import { milestoneService } from '../../services/milestoneService';
import { createCompletionPaymentIntent, confirmPayment, getProjectMilestonePayments } from '../../services/paymentService';
import { useAuth } from '../../contexts/AuthContext';
import { sendPaymentSuccessEmail, sendPaymentFailureEmail } from '../../services/emailService';

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

const statusConfig = {
  completed: { label: 'Completed', bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500', ring: 'ring-emerald-200' },
  active: { label: 'In Progress', bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500', ring: 'ring-amber-200' },
  pending: { label: 'Pending', bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-300', ring: 'ring-gray-200' },
};

// ─── Completion Payment Modal ─────────────────────────────────────────────────
const CompletionPaymentForm = ({ clientSecret, paymentIntentId, completionAmount, currency, onSuccess, onError, onClose }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!stripe || !elements) return;
    setIsProcessing(true);
    try {
      const { error, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: { return_url: `${window.location?.origin}/payment-confirmation` },
        redirect: 'if_required',
      });
      if (error) {
        onError(error?.message ?? 'Payment failed. Please try again.');
        return;
      }
      if (paymentIntent?.status === 'succeeded') {
        await confirmPayment(paymentIntent?.id);
        onSuccess(paymentIntent);
      } else {
        onError('Payment was not completed. Please try again.');
      }
    } catch (err) {
      onError(err?.message ?? 'An unexpected error occurred.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-4 p-4 rounded-xl" style={{ background: 'linear-gradient(to right, rgba(16,185,129,0.08), rgba(5,150,105,0.08))', border: '1px solid rgba(16,185,129,0.2)' }}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-800">Completion Payment</p>
            <p className="text-xs text-gray-500 mt-0.5">70% balance due upon service completion</p>
          </div>
          <span className="text-xl font-bold text-emerald-600">${completionAmount?.toLocaleString()} <span className="text-xs font-normal text-gray-400">{currency?.toUpperCase()}</span></span>
        </div>
      </div>
      <PaymentElement options={{ layout: 'tabs', paymentMethodOrder: ['card', 'apple_pay', 'google_pay'] }} />
      <div className="flex gap-3 mt-5">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 shadow-md"
          style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
          {isProcessing ? (
            <>
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Processing...
            </>
          ) : (
            <>
              <Icon name="Lock" size={15} />
              Pay ${completionAmount?.toLocaleString()}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ProjectMilestoneTracker = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams?.get('project');
  const { user } = useAuth();

  const [project, setProject] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMilestone, setSelectedMilestone] = useState(null);

  // Milestone payment state
  const [milestonePayments, setMilestonePayments] = useState(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionPaymentData, setCompletionPaymentData] = useState(null);
  const [isInitiatingCompletion, setIsInitiatingCompletion] = useState(false);
  const [completionError, setCompletionError] = useState(null);
  const [completionSuccess, setCompletionSuccess] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      try {
        const projects = await projectService?.getAll();
        const target = projectId
          ? projects?.find(p => p?.id === projectId)
          : projects?.[0];

        if (target && isMounted) {
          setProject(target);
          const milestonesData = await milestoneService?.getByProjectId(target?.id);
          if (isMounted) {
            setMilestones(milestonesData || []);
            const active = milestonesData?.find(m => m?.status === 'active');
            if (active) setSelectedMilestone(active);
          }
          // Load milestone payment status
          try {
            const payments = await getProjectMilestonePayments(target?.id);
            if (isMounted) setMilestonePayments(payments);
          } catch (_) {
            // Payment data optional — don't block render
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadData();
    return () => { isMounted = false; };
  }, [projectId]);

  useEffect(() => {
    if (!project?.id) return;
    const unsubscribe = milestoneService?.subscribeToChanges(project?.id, (payload) => {
      if (payload?.eventType === 'INSERT') {
        setMilestones(prev => [...prev, payload?.new]?.sort((a, b) => a?.sequenceOrder - b?.sequenceOrder));
      } else if (payload?.eventType === 'UPDATE') {
        setMilestones(prev => prev?.map(m => m?.id === payload?.new?.id ? { ...m, ...payload?.new } : m));
      } else if (payload?.eventType === 'DELETE') {
        setMilestones(prev => prev?.filter(m => m?.id !== payload?.old?.id));
      }
    });
    return () => unsubscribe?.();
  }, [project?.id]);

  const stats = useMemo(() => {
    const completed = milestones?.filter(m => m?.status === 'completed')?.length || 0;
    const total = milestones?.length || 0;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percentage };
  }, [milestones]);

  const allMilestonesComplete = stats?.total > 0 && stats?.completed === stats?.total;

  const formatDate = (date) => {
    if (!date) return null;
    return new Date(date)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleToggle = (milestone) => {
    setSelectedMilestone(prev => prev?.id === milestone?.id ? null : milestone);
  };

  const handleInitiateCompletionPayment = async () => {
    if (!project?.id) return;
    setIsInitiatingCompletion(true);
    setCompletionError(null);
    try {
      const result = await createCompletionPaymentIntent({
        projectId: project?.id,
        userId: user?.id ?? null,
        vendorName: project?.vendorName ?? null,
        customerEmail: user?.email ?? null,
        customerName: user?.user_metadata?.full_name ?? null,
      });

      if (result?.alreadyExists) {
        if (result?.status === 'succeeded') {
          setCompletionSuccess(true);
          return;
        }
        setCompletionError('A completion payment is already in progress.');
        return;
      }

      setCompletionPaymentData(result);
      setShowCompletionModal(true);
    } catch (err) {
      setCompletionError(err?.message ?? 'Failed to initiate completion payment.');
    } finally {
      setIsInitiatingCompletion(false);
    }
  };

  const handleCompletionSuccess = async (paymentIntent) => {
    setShowCompletionModal(false);
    setCompletionSuccess(true);
    setCompletionPaymentData(null);
    // Send payment success email to the user
    if (user?.email) {
      sendPaymentSuccessEmail(user?.email, {
        recipientName: user?.user_metadata?.full_name || '',
        projectName: project?.name || 'Service Stack',
        vendorName: project?.vendorName || '',
        amount: completionPaymentData?.completionAmount || 0,
        milestoneType: 'completion',
        paymentIntentId: paymentIntent?.id,
        projectId: project?.id,
        isVendor: false,
      })?.catch(() => {});
    }
    // Refresh payment status
    try {
      const payments = await getProjectMilestonePayments(project?.id);
      setMilestonePayments(payments);
    } catch (_) {}
  };

  const handleCompletionError = (message) => {
    setCompletionError(message);
    // Send payment failure email to the user
    if (user?.email) {
      sendPaymentFailureEmail(user?.email, {
        recipientName: user?.user_metadata?.full_name || '',
        projectName: project?.name || 'Service Stack',
        vendorName: project?.vendorName || '',
        amount: completionPaymentData?.completionAmount || 0,
        milestoneType: 'completion',
        errorMessage: message,
        projectId: project?.id,
        isVendor: false,
      })?.catch(() => {});
    }
  };

  const stripeOptions = completionPaymentData?.clientSecret ? {
    clientSecret: completionPaymentData?.clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#10b981',
        colorBackground: '#ffffff',
        colorText: '#1f2937',
        colorDanger: '#ef4444',
        fontFamily: 'Inter, system-ui, sans-serif',
        borderRadius: '12px',
      },
    },
  } : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-32 flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: '#F06F6B' }} />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-32 pb-16">
        {/* Hero Header */}
        <div
          className="relative overflow-hidden py-10 px-4 sm:px-6 lg:px-8 mb-8"
          style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B, #E0528A)' }}
        >
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/5 blur-3xl" />
            <div className="absolute bottom-0 -left-8 w-40 h-40 rounded-full bg-white/5 blur-2xl" />
          </div>
          <div className="relative max-w-5xl mx-auto">
            <button
              onClick={() => navigate('/all-projects')}
              className="flex items-center gap-2 text-white/80 hover:text-white text-sm mb-4 transition-colors"
            >
              <Icon name="ArrowLeft" size={16} />
              Back to My Stacks
            </button>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">
                  {project?.name || 'Stack Milestones'}
                </h1>
                <p className="text-white/75 text-sm capitalize">
                  {project?.category?.replace(/-/g, ' ')} · Service Stack
                </p>
              </div>
              <div className="flex items-center gap-3 bg-white/15 backdrop-blur-sm rounded-2xl px-5 py-3 self-start sm:self-auto">
                <div className="text-right">
                  <div className="text-white font-bold text-xl leading-none">{stats?.percentage}%</div>
                  <div className="text-white/70 text-xs mt-0.5">Complete</div>
                </div>
                <div className="w-px h-8 bg-white/25" />
                <div className="text-right">
                  <div className="text-white font-bold text-xl leading-none">{stats?.completed}/{stats?.total}</div>
                  <div className="text-white/70 text-xs mt-0.5">Milestones</div>
                </div>
              </div>
            </div>
            <div className="mt-5 w-full bg-white/20 rounded-full h-2">
              <div
                className="h-2 rounded-full bg-white transition-all duration-700"
                style={{ width: `${stats?.percentage}%` }}
              />
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Milestone Payment Status Banner */}
          {milestonePayments && (
            <div className="mb-6 rounded-2xl overflow-hidden border border-gray-100 shadow-sm bg-white">
              <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Icon name="CreditCard" size={15} className="text-gray-500" />
                  Milestone Payment Status
                </h3>
              </div>
              <div className="grid grid-cols-2 divide-x divide-gray-100">
                {/* Deposit */}
                <div className="p-4 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${milestonePayments?.depositPaid ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                    <Icon name={milestonePayments?.depositPaid ? 'CheckCircle2' : 'Clock'} size={18} className={milestonePayments?.depositPaid ? 'text-emerald-600' : 'text-amber-600'} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">30% Deposit</p>
                    <p className={`text-xs font-medium ${milestonePayments?.depositPaid ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {milestonePayments?.depositPaid ? `Paid · $${milestonePayments?.deposit?.amount?.toLocaleString()}` : 'Pending'}
                    </p>
                  </div>
                </div>
                {/* Completion */}
                <div className="p-4 flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${milestonePayments?.completionPaid ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                    <Icon name={milestonePayments?.completionPaid ? 'CheckCircle2' : 'Hourglass'} size={18} className={milestonePayments?.completionPaid ? 'text-emerald-600' : 'text-gray-400'} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">70% Completion</p>
                    <p className={`text-xs font-medium ${milestonePayments?.completionPaid ? 'text-emerald-600' : 'text-gray-400'}`}>
                      {milestonePayments?.completionPaid ? `Paid · $${milestonePayments?.completion?.amount?.toLocaleString()}` : 'Due on completion'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Completion Payment CTA — shown when all milestones done and deposit paid but completion not yet paid */}
          {allMilestonesComplete && milestonePayments?.depositPaid && !milestonePayments?.completionPaid && !completionSuccess && (
            <div className="mb-6 rounded-2xl p-5 border border-emerald-200 bg-emerald-50 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Icon name="PartyPopper" size={20} className="text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-emerald-800 text-sm">All milestones complete!</h4>
                <p className="text-xs text-emerald-700 mt-0.5">Your service is done. Please pay the 70% completion balance to finalize.</p>
                {completionError && (
                  <p className="text-xs text-red-600 mt-1">{completionError}</p>
                )}
              </div>
            </div>
          )}

          {/* Completion Success Banner */}
          {(completionSuccess || milestonePayments?.completionPaid) && (
            <div className="mb-6 rounded-2xl p-5 border border-emerald-200 bg-emerald-50 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Icon name="CheckCircle2" size={20} className="text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-emerald-800 text-sm">Payment complete!</h4>
                <p className="text-xs text-emerald-700 mt-0.5">All milestone payments have been processed. Your stack is fully paid.</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Left: Milestone List */}
            <div className="lg:col-span-2 space-y-3">
              <h2 className="text-base font-semibold text-foreground mb-4">Milestone Timeline</h2>
              {milestones?.length === 0 ? (
                <div className="bg-card border border-border rounded-2xl p-10 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                    <Icon name="Flag" size={24} className="text-text-secondary" />
                  </div>
                  <p className="text-foreground font-medium mb-1">No milestones yet</p>
                  <p className="text-text-secondary text-sm">Milestones will appear here once your service stack is underway.</p>
                </div>
              ) : (
                milestones?.map((milestone, index) => {
                  const sc = statusConfig?.[milestone?.status] || statusConfig?.pending;
                  const isSelected = selectedMilestone?.id === milestone?.id;
                  return (
                    <div key={milestone?.id} className="relative">
                      {index < milestones?.length - 1 && (
                        <div className="absolute left-5 top-12 w-px h-4 bg-border z-0" />
                      )}
                      <div
                        className={`relative z-10 bg-card border rounded-2xl p-4 transition-all duration-200 hover:shadow-md cursor-pointer ${
                          isSelected ? 'border-[#F06F6B] shadow-md' : 'border-border hover:border-[#F06F6B]/40'
                        }`}
                      >
                        <div className="flex items-center gap-4" onClick={() => handleToggle(milestone)}>
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ring-4 ${sc?.ring} ${
                            milestone?.status === 'completed' ? 'bg-emerald-500' :
                            milestone?.status === 'active' ? 'bg-amber-500' : 'bg-gray-200'
                          }`}>
                            {milestone?.status === 'completed' ? (
                              <Icon name="Check" size={16} color="white" />
                            ) : milestone?.status === 'active' ? (
                              <Icon name="Clock" size={16} color="white" />
                            ) : (
                              <span className="text-xs font-bold text-gray-400">{index + 1}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <h3 className="font-semibold text-foreground text-sm truncate">{milestone?.name}</h3>
                              <span className={`flex-shrink-0 text-xs font-medium px-2.5 py-1 rounded-full ${sc?.bg} ${sc?.text}`}>
                                {sc?.label}
                              </span>
                            </div>
                            {milestone?.description && (
                              <p className="text-text-secondary text-xs mt-0.5 truncate">{milestone?.description}</p>
                            )}
                            <div className="flex items-center gap-3 mt-1.5">
                              {milestone?.completedDate && (
                                <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                                  <Icon name="CheckCircle2" size={11} />
                                  {formatDate(milestone?.completedDate)}
                                </span>
                              )}
                              {milestone?.estimatedDate && !milestone?.completedDate && (
                                <span className="text-xs text-text-secondary flex items-center gap-1">
                                  <Icon name="Calendar" size={11} />
                                  Est. {formatDate(milestone?.estimatedDate)}
                                </span>
                              )}
                              {milestone?.amount && (
                                <span className="text-xs font-semibold text-foreground">
                                  ${milestone?.amount?.toLocaleString()}
                                </span>
                              )}
                            </div>
                          </div>
                          <Icon
                            name={isSelected ? 'ChevronUp' : 'ChevronDown'}
                            size={16}
                            className="text-text-secondary flex-shrink-0"
                          />
                        </div>

                        {isSelected && (
                          <div className="mt-4 pt-4 border-t border-border">
                            {milestone?.description && (
                              <p className="text-sm text-text-secondary mb-3">{milestone?.description}</p>
                            )}
                            <div className="flex flex-wrap gap-2">
                              {milestone?.status === 'active' && (
                                <button
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all hover:opacity-90"
                                  style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B)' }}
                                >
                                  <Icon name="CheckCircle" size={12} color="white" />
                                  Mark Complete
                                </button>
                              )}
                              {(milestone?.status === 'active' || milestone?.status === 'pending') && (
                                <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-border text-text-secondary hover:text-foreground hover:bg-muted transition-all">
                                  <Icon name="MessageSquare" size={12} />
                                  Message Vendor
                                </button>
                              )}
                              {milestone?.status === 'active' && (
                                <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-border text-text-secondary hover:text-foreground hover:bg-muted transition-all">
                                  <Icon name="Upload" size={12} />
                                  Upload Docs
                                </button>
                              )}
                              {milestone?.status === 'completed' && (
                                <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-border text-text-secondary hover:text-foreground hover:bg-muted transition-all">
                                  <Icon name="Download" size={12} />
                                  Download Receipt
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Right: Project Info */}
            <div className="space-y-4">
              <div className="bg-card border border-border rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-foreground mb-4">Stack Summary</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary">Status</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${
                      project?.status === 'active' ? 'bg-[#F06F6B]/10 text-[#F06F6B]' :
                      project?.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    }`}>{project?.status}</span>
                  </div>
                  {project?.budgetMin && project?.budgetMax && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-secondary">Budget</span>
                      <span className="font-medium text-foreground">
                        ${project?.budgetMin?.toLocaleString()} – ${project?.budgetMax?.toLocaleString()}
                      </span>
                    </div>
                  )}
                  {project?.deadline && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-secondary">Deadline</span>
                      <span className="font-medium text-foreground">{formatDate(project?.deadline)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary">Vendors</span>
                    <span className="font-medium text-foreground">{project?.vendorCount || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary">Quotes</span>
                    <span className="font-medium text-foreground">{project?.quotesReceived || 0}</span>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-foreground mb-3">Quick Actions</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => navigate(`/project-comparison?project=${project?.id}`)}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium border border-border text-text-secondary hover:text-foreground hover:bg-muted transition-all"
                  >
                    <Icon name="BarChart3" size={15} />
                    View Comparison
                  </button>
                  <button
                    onClick={() => navigate('/buyer-vendor-messaging-interface')}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium border border-border text-text-secondary hover:text-foreground hover:bg-muted transition-all"
                  >
                    <Icon name="MessageSquare" size={15} />
                    Message Vendor
                  </button>
                  <button
                    onClick={() => navigate(`/create-project-flow?edit=${project?.id}`)}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium border border-border text-text-secondary hover:text-foreground hover:bg-muted transition-all"
                  >
                    <Icon name="Edit" size={15} />
                    Edit Stack
                  </button>
                </div>
              </div>

              <div className="bg-card border border-border rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-foreground mb-3">Legend</h3>
                <div className="space-y-2">
                  {[
                    { color: 'bg-emerald-500', label: 'Completed' },
                    { color: 'bg-amber-500', label: 'In Progress' },
                    { color: 'bg-gray-300', label: 'Pending' },
                  ]?.map(item => (
                    <div key={item?.label} className="flex items-center gap-2.5 text-sm text-text-secondary">
                      <div className={`w-3 h-3 rounded-full ${item?.color}`} />
                      {item?.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </main>

      {/* Completion Payment Modal */}
      {showCompletionModal && completionPaymentData?.clientSecret && stripeOptions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" style={{ background: 'linear-gradient(to right, #10b981, #059669)' }}>
              <div className="flex items-center gap-2">
                <Icon name="CreditCard" size={18} className="text-white" />
                <h3 className="text-base font-bold text-white">Completion Payment</h3>
              </div>
              <button onClick={() => setShowCompletionModal(false)} className="text-white/70 hover:text-white transition-colors">
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-6">
              {completionError && (
                <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2">
                  <Icon name="AlertCircle" size={15} className="text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700">{completionError}</p>
                </div>
              )}
              <Elements stripe={stripePromise} options={stripeOptions}>
                <CompletionPaymentForm
                  clientSecret={completionPaymentData?.clientSecret}
                  paymentIntentId={completionPaymentData?.paymentIntentId}
                  completionAmount={completionPaymentData?.completionAmount}
                  currency={completionPaymentData?.currency}
                  onSuccess={handleCompletionSuccess}
                  onError={handleCompletionError}
                  onClose={() => setShowCompletionModal(false)} />
              </Elements>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectMilestoneTracker;